import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { CheckCircleIcon, MailIcon } from '@heroicons/react/outline';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { EmailPreviewFrame } from 'components/EmailPreviewFrame';
import { SizeEnum } from 'utils';
import { getEventTypeMeta } from 'utils/klaviyoEventTypeMeta';
import { returnEmailTemplateTypes } from 'containers/NestedContainers/Tracking/data/templates-data';
import {
  GET_RETURN_KLAVIYO_TEMPLATE_STYLES,
  GET_RETURN_KLAVIYO_TEMPLATE_PREVIEWS,
} from 'gql/queries';

// Same reasoning as ChooseKlaviyoTemplateModal: the backend renders previews in small batches
// and publishes each batch as soon as it's ready, so polling gets progressively fuller results.
const TEMPLATE_PREVIEW_POLL_INTERVAL_MS = 1500;
const TOTAL_RETURN_FLOW_COUNT = 6;

type TemplateStyle = { id: string; name: string; description: string };
type TemplatePreview = { eventType: string; flowName: string; html: string };

type ChooseReturnKlaviyoTemplateModalProps = {
  show: boolean;
  setShowModal: (value: boolean) => void;
  currentTemplateStyle?: string | null;
  onSelectTemplateStyle: (style: string) => void;
};

// Its own component (rather than inline in a .map) so each card can fetch its own style's
// preview without breaking the rules of hooks — mirrors ChooseKlaviyoTemplateModal's
// TemplateStyleCard, but previews RETURN_REVIEWED instead of DELIVERED (returns has no
// "delivered" event).
const ReturnTemplateStyleCard = ({
  style,
  isActive,
  onSelect,
}: {
  style: TemplateStyle;
  isActive: boolean;
  onSelect: (styleId: string) => void;
}) => {
  const [ready, setReady] = useState(false);
  const { data } = useQuery(GET_RETURN_KLAVIYO_TEMPLATE_PREVIEWS, {
    variables: { templateStyle: style.id },
    pollInterval: ready ? 0 : TEMPLATE_PREVIEW_POLL_INTERVAL_MS,
  });
  useEffect(() => {
    if (data?.returnKlaviyoTemplatePreviews?.status === 'DONE') {
      setReady(true);
    }
  }, [data]);
  const representativePreview: TemplatePreview | undefined =
    data?.returnKlaviyoTemplatePreviews?.previews?.find(
      (preview: TemplatePreview) => preview.eventType === 'RETURN_REVIEWED',
    );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(style.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(style.id);
        }
      }}
      className={`relative cursor-pointer overflow-hidden rounded-xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        isActive ? 'border-2 border-blue-600' : 'border-slate-200'
      }`}
    >
      {isActive ? (
        <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-blue-700">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Active
        </div>
      ) : null}
      <div className="mb-3 h-64 w-full overflow-hidden rounded-lg border border-[#d8dfeb] bg-[#f8fbff]">
        {representativePreview ? (
          <EmailPreviewFrame
            html={representativePreview.html}
            title={`${style.name} return reviewed preview`}
            className="h-64 w-full"
            fit="contain"
          />
        ) : (
          <div className="flex h-64 items-center justify-center text-xs text-[#64748b]">
            Loading preview…
          </div>
        )}
      </div>
      <h3 className="text-base font-semibold text-[#0b1c30]">{style.name}</h3>
      <p className="mt-1.5 text-sm leading-5 text-[#64748b]">
        {style.description}
      </p>
    </div>
  );
};

export const ChooseReturnKlaviyoTemplateModal = ({
  show,
  setShowModal,
  currentTemplateStyle,
  onSelectTemplateStyle,
}: ChooseReturnKlaviyoTemplateModalProps) => {
  const [step, setStep] = useState<'select' | 'preview'>('select');
  const [previewingStyle, setPreviewingStyle] = useState<string | null>(null);

  const { data: stylesData, loading: stylesLoading } = useQuery(
    GET_RETURN_KLAVIYO_TEMPLATE_STYLES,
    { skip: !show, fetchPolicy: 'network-only' },
  );

  const [previewsReady, setPreviewsReady] = useState(false);
  useEffect(() => {
    setPreviewsReady(false);
  }, [previewingStyle]);

  const { data: previewsData, loading: previewsLoading } = useQuery(
    GET_RETURN_KLAVIYO_TEMPLATE_PREVIEWS,
    {
      variables: { templateStyle: previewingStyle },
      skip: !show || step !== 'preview' || !previewingStyle,
      pollInterval: previewsReady ? 0 : TEMPLATE_PREVIEW_POLL_INTERVAL_MS,
    },
  );
  useEffect(() => {
    if (previewsData?.returnKlaviyoTemplatePreviews?.status === 'DONE') {
      setPreviewsReady(true);
    }
  }, [previewsData]);

  const styles: TemplateStyle[] = stylesData?.returnKlaviyoTemplateStyles || [];
  const previews: TemplatePreview[] =
    previewsData?.returnKlaviyoTemplatePreviews?.previews || [];

  const handleClose = () => {
    setShowModal(false);
    setStep('select');
    setPreviewingStyle(null);
  };

  const handlePickStyle = (styleId: string) => {
    setPreviewingStyle(styleId);
    setStep('preview');
  };

  const handleConfirm = () => {
    if (!previewingStyle) return;
    onSelectTemplateStyle(previewingStyle);
    handleClose();
  };

  return (
    <Modal
      openModal={handleClose}
      open={show}
      containerClass="w-full max-w-5xl !p-0 overflow-hidden"
      iconClassName="text-[#64748b] hover:text-[#0b1c30]"
    >
      <div className="px-6 pb-6 pt-1">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
            <MailIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0b1c30]">
              {step === 'select'
                ? 'Choose an email template'
                : `Preview all ${TOTAL_RETURN_FLOW_COUNT} emails`}
            </h2>
            <p className="mt-1 text-sm leading-6 text-[#64748b]">
              {step === 'select'
                ? `Pick a design below to preview all ${TOTAL_RETURN_FLOW_COUNT} return notification emails before applying it.`
                : 'Review every notification email for this template, then confirm to use it.'}
            </p>
          </div>
        </div>

        {step === 'select' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {stylesLoading ? (
              <div className="col-span-full flex min-h-[160px] items-center justify-center">
                <div
                  style={{
                    borderColor: 'rgba(37,99,235, 0.2)',
                    borderTopColor: '#2564eb',
                  }}
                  className="inline-block h-10 w-10 animate-spin rounded-full border-4"
                  role="status"
                  aria-label="Loading template styles"
                />
              </div>
            ) : (
              styles.map((style) => (
                <ReturnTemplateStyleCard
                  key={style.id}
                  style={style}
                  isActive={style.id === currentTemplateStyle}
                  onSelect={handlePickStyle}
                />
              ))
            )}
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setStep('select')}
              className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#2563eb] hover:text-[#1d4ed8]"
            >
              ← Back to templates
            </button>
            {previewsLoading && !previews.length ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <div
                  style={{
                    borderColor: 'rgba(37,99,235, 0.2)',
                    borderTopColor: '#2564eb',
                  }}
                  className="inline-block h-10 w-10 animate-spin rounded-full border-4"
                  role="status"
                  aria-label="Loading template previews"
                />
              </div>
            ) : (
              <>
                {!previewsReady ? (
                  <p className="mb-3 flex items-center gap-2 text-xs text-[#64748b]">
                    <span
                      style={{
                        borderColor: 'rgba(37,99,235, 0.25)',
                        borderTopColor: '#2564eb',
                      }}
                      className="inline-block h-3 w-3 animate-spin rounded-full border-2"
                    />
                    Loading previews… {previews.length}/
                    {TOTAL_RETURN_FLOW_COUNT}
                  </p>
                ) : null}
                <div className="grid max-h-[55vh] grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-3">
                  {previews.map((preview) => {
                    const meta = getEventTypeMeta(
                      preview.eventType,
                      returnEmailTemplateTypes,
                    );
                    const Icon = meta.icon;
                    return (
                      <div
                        key={preview.eventType}
                        className="overflow-hidden rounded-lg border border-[#d8dfeb] bg-[#f8fbff]"
                      >
                        <div className="flex items-center gap-2 border-b border-[#d8dfeb] bg-white px-3 py-2">
                          {Icon ? (
                            <Icon className="h-4 w-4 text-[#334155]" />
                          ) : meta.imgSrc ? (
                            <img src={meta.imgSrc} alt="" className="h-4 w-4" />
                          ) : null}
                          <span className="truncate text-xs font-semibold text-[#0b1c30]">
                            {meta.label}
                          </span>
                        </div>
                        <EmailPreviewFrame
                          html={preview.html}
                          title={preview.flowName}
                          className="w-full"
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 border-t border-[#e5ebf4] bg-[#f8fbff] px-6 py-4">
        <button
          type="button"
          onClick={handleClose}
          className="rounded-lg border border-[#d8dfeb] bg-white px-4 py-2 text-sm font-semibold text-[#334155] shadow-sm transition hover:bg-[#eff4ff]"
        >
          Cancel
        </button>
        {step === 'preview' ? (
          <Button
            onClick={handleConfirm}
            size={SizeEnum.md}
            color="primary"
            variant="contained"
            className="rounded-lg"
          >
            Use this template
          </Button>
        ) : null}
      </div>
    </Modal>
  );
};
